import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { ThemeToggle } from '@/components/ThemeToggle'
import {
  usePostApiUsersEmailChangeConfirmation,
  usePostApiUsersUserIdConfirmationToken,
} from '@/api/generated/public/public'
import { isServerUnreachable, parseApiError, translateApiErrorCode } from '@/lib/apiError'
import { useAuthStore } from '@/store/auth.store'
import { Button } from '@/components/shadcn/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card'
import { WallpaperBackground } from '@/pages/login/components/WallpaperBackground'

type ConfirmationState = 'pending' | 'success' | 'error'

export default function EmailConfirmationPage() {
  const { t } = useTranslation('login')
  const [searchParams] = useSearchParams()
  const clearAuth = useAuthStore((s) => s.clearAuth)

  const userId = searchParams.get('user') ?? ''
  const token = searchParams.get('token') ?? ''
  // Only email-change links carry the new address; registration links don't.
  const newEmail = searchParams.get('email')
  const isEmailChange = newEmail !== null

  // Tracked separately from the mutation's own isPending: the generated hook shares a
  // static mutationKey across every usage, so its status can flicker under React
  // StrictMode's double-invoke. Our own state only ever moves pending -> success/error.
  const [state, setState] = useState<ConfirmationState>('pending')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleError = (error: unknown) => {
    // Only "address taken in the meantime" gets its own text; every other failure means a bad link.
    const { errorCode } = parseApiError(error)
    if (isServerUnreachable(error)) {
      setErrorMessage(t('serverUnreachable'))
    } else if (errorCode === 'UserEmailAlreadyTaken') {
      setErrorMessage(translateApiErrorCode(errorCode))
    } else {
      setErrorMessage(t('emailConfirmationPage.errors.linkInvalid'))
    }
    setState('error')
  }

  const { mutate: confirmEmail } = usePostApiUsersUserIdConfirmationToken({
    mutation: {
      onSuccess: () => setState('success'),
      onError: handleError,
    },
  })

  const { mutate: confirmEmailChange } = usePostApiUsersEmailChangeConfirmation({
    mutation: {
      onSuccess: () => {
        // Backend revoked all sessions; drop the local one too so login uses the new address.
        clearAuth()
        setState('success')
      },
      onError: handleError,
    },
  })

  // Confirmation is a side effect of opening the link, not a user action — trigger it
  // once on mount. The ref guards against React StrictMode's double-invoke in dev.
  const triggered = useRef(false)
  useEffect(() => {
    if (triggered.current) return
    triggered.current = true

    if (!userId || !token || newEmail === '') {
      setErrorMessage(t('emailConfirmationPage.errors.linkInvalid'))
      setState('error')
      return
    }
    if (newEmail !== null) {
      confirmEmailChange({ data: { userId, newEmail, token } })
    } else {
      confirmEmail({ userId, token })
    }
  }, [userId, token, newEmail, confirmEmail, confirmEmailChange, t])

  return (
    <WallpaperBackground>
      <Card className="w-full max-w-sm">
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-2xl">{t('emailConfirmationPage.title')}</CardTitle>
            <CardDescription>
              {state === 'success'
                ? t(isEmailChange ? 'emailConfirmationPage.descriptionEmailChangeSuccess' : 'emailConfirmationPage.descriptionSuccess')
                : state === 'error'
                  ? t('emailConfirmationPage.descriptionError')
                  : t('emailConfirmationPage.descriptionPending')}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
            {state !== 'pending' && (
              <Link to="/login">
                <Button className="w-full">{t('emailConfirmationPage.goToLogin')}</Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    </WallpaperBackground>
  )
}
